import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { ExternalLink, FileText, Loader2, RotateCw } from "lucide-react";
import { powtoonBE } from "@/lib/powtoon-be/client";
import type { ScenarioDisplaySource } from "@/lib/powtoon-be/types";
import { normalizeDisplayUrl } from "@/lib/scenarios/urlEmbedCheck";
import { isPdfDisplaySource } from "@/lib/scenarios/display";
import { documentBytesUrl } from "@/lib/scenarios/documentProxy";
import { Button } from "@/components/ui/button";
import {
  PdfDocument,
  type PdfDocumentHandle,
  type PdfFailure,
} from "@/components/session/PdfDocument";

/**
 * Imperative surface a parent (currently the D-ID session frame) uses to drive
 * and read the document on screen. Only PDFs are pageable; every other kind
 * reports `kind: "other"` so a caller can say so instead of failing silently.
 */
export type DocumentControl = {
  kind: "pdf" | "other";
  /** Page on screen right now, 0 when the document isn't pageable. */
  currentPage(): number;
  goToPage(page: number): { page: number; total: number; learnerMoved: boolean };
  readCurrentPage(): Promise<{
    page: number;
    total: number;
    text: string;
    learnerMoved: boolean;
  }>;
};



function inferKind(
  url: string,
  mime?: string,
): "pdf" | "image" | "video" | "office" | "web" {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (m === "application/pdf") return "pdf";
  if (
    m.includes("officedocument.presentationml") ||
    m.includes("officedocument.wordprocessingml") ||
    m.includes("officedocument.spreadsheetml") ||
    m === "application/vnd.ms-powerpoint" ||
    m === "application/msword" ||
    m === "application/vnd.ms-excel"
  )
    return "office";
  const u = url.toLowerCase();
  if (u.match(/\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/)) return "image";
  if (u.match(/\.(mp4|webm|ogg|mov)(\?|$)/)) return "video";
  if (u.match(/\.pdf(\?|$)/)) return "pdf";
  if (u.match(/\.(pptx?|docx?|xlsx?)(\?|$)/)) return "office";
  return "web";
}

export function DisplayDocument({
  scenarioId,
  source,
  className,
  publicLinkToken,
  controlRef,
}: {
  scenarioId: string;
  source: ScenarioDisplaySource;
  className?: string;
  publicLinkToken?: string;
  /** Optional imperative handle for avatar-driven paging (PDF only). */
  controlRef?: Ref<DocumentControl>;
}) {
  const [url, setUrl] = useState<string | null>(
    source.kind === "url" ? normalizeDisplayUrl(source.url) : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  // A PDF that cannot be opened is reported, never downgraded to the browser's
  // built-in reader: that reader has no page control and no page text, so the
  // avatar could not present or follow the document.
  const [pdfFailure, setPdfFailure] = useState<PdfFailure | null>(null);
  // Bumped by "Try again" to remount the viewer with a fresh load.
  const [pdfAttempt, setPdfAttempt] = useState(0);
  // Stable identity: the viewer keys its document-loading effect off this, so an
  // inline arrow here would reload the PDF on every re-render of the session.
  const handlePdfFailure = useCallback((f: PdfFailure) => setPdfFailure(f), []);
  const retryPdf = useCallback(() => {
    setPdfFailure(null);
    setPdfAttempt((n) => n + 1);
  }, []);
  const pdfRef = useRef<PdfDocumentHandle | null>(null);


  const mime = source.kind === "upload" ? source.mime : undefined;

  useImperativeHandle(
    controlRef,
    () => ({
      get kind() {
        return pdfRef.current ? ("pdf" as const) : ("other" as const);
      },
      currentPage: () => pdfRef.current?.currentPage() ?? 0,

      goToPage: (page: number) =>
        pdfRef.current?.goToPage(page) ?? { page: 0, total: 0, learnerMoved: false },
      readCurrentPage: async () =>
        pdfRef.current
          ? await pdfRef.current.readCurrentPage()
          : { page: 0, total: 0, text: "", learnerMoved: false },

    }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setVideoReady(false);
    setIframeBlocked(false);
    setFrameLoaded(false);
    if (source.kind === "url") {
      setUrl(normalizeDisplayUrl(source.url));
      return;
    }
    setUrl(null);
    setError(null);
    powtoonBE.attachments
      .get(scenarioId, source.object_id, { publicLinkToken })
      .then((att) => {
        if (cancelled) return;
        const fileUrl = att?.file ?? att?.file_url;
        if (!fileUrl) {
          setError("Document is unavailable.");
          return;
        }
        setUrl(fileUrl);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || "Failed to load document.");
      });
    return () => {
      cancelled = true;
    };
  }, [scenarioId, source, publicLinkToken]);

  // Sites that refuse framing never fire `onError` — if no load event lands
  // within a few seconds, fall back to the "open in a new tab" card. PDFs never
  // reach the iframe, so this only guards embedded web pages.
  useEffect(() => {
    if (!url || frameLoaded || iframeBlocked) return;
    if (inferKind(url, mime) !== "web") return;
    const t = setTimeout(() => setIframeBlocked(true), 6000);
    return () => clearTimeout(t);
  }, [url, frameLoaded, iframeBlocked, mime]);


  if (error) {
    return (
      <div className={"flex h-full w-full items-center justify-center bg-muted/30 px-4 text-center " + (className ?? "")}>
        <p className="text-xs text-muted-foreground">{error}</p>
      </div>
    );
  }
  if (!url) {
    return (
      <div className={"flex h-full w-full items-center justify-center bg-muted/30 " + (className ?? "")}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const kind = inferKind(url, mime);
  const wrapper = "h-full w-full bg-background " + (className ?? "");
  // A PDF is decided from the scenario's own source (saved file name / type, or
  // the configured link) first — the attachment download URL it resolves to is
  // temporary and often carries no `.pdf` suffix at all.
  const isPdf = isPdfDisplaySource(source) || kind === "pdf";
  if (isPdf) {
    if (pdfFailure) {
      return <PdfFailureCta url={url} failure={pdfFailure} onRetry={retryPdf} className={wrapper} />;
    }
    return (
      <PdfDocument
        key={pdfAttempt}
        url={documentBytesUrl(url)}
        openUrl={url}
        className={className}
        onFailure={handlePdfFailure}
        ref={pdfRef}
      />
    );
  }

  if (kind === "image") {
    return (
      <div className={wrapper + " flex items-center justify-center overflow-auto"}>
        <img src={url} alt="" className="max-h-full max-w-full object-contain" />
      </div>
    );
  }
  if (kind === "video") {
    return (
      <div className={wrapper + " relative flex items-center justify-center bg-black"}>
        <video
          src={url}
          controls
          preload="auto"
          onLoadedData={() => setVideoReady(true)}
          onCanPlay={() => setVideoReady(true)}
          onError={() => setError("Document is unavailable.")}
          className={
            "max-h-full max-w-full transition-opacity duration-200 " +
            (videoReady ? "opacity-100" : "opacity-0")
          }
        />
        {!videoReady && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    );
  }
  if (kind === "office") {
    const viewerSrc =
      "https://view.officeapps.live.com/op/embed.aspx?src=" +
      encodeURIComponent(url);
    return (
      <div className={wrapper + " flex flex-col"}>
        <iframe
          src={viewerSrc}
          title="Learner document"
          className="flex-1 w-full border-0"
        />
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border-t bg-background/80 text-center"
        >
          Can't see the document? Open in a new tab
        </a>
      </div>
    );
  }
  // Embedded web pages only; if the browser fires an error event (CSP block),
  // or nothing ever loads (X-Frame-Options / CSP frame-ancestors never fire
  // an error event), show a clean "Open in new tab" fallback.
  if (iframeBlocked) {
    return <BlockedDocCta url={url} className={wrapper} />;
  }
  return (
    <div className={wrapper + " flex flex-col"}>
      <div className="relative flex-1 min-h-0">
        <iframe
          src={url}
          title="Learner document"
          className="h-full w-full border-0"
          sandbox={kind === "web" ? "allow-scripts allow-same-origin allow-popups allow-forms" : undefined}
          onLoad={() => setFrameLoaded(true)}
          onError={() => setIframeBlocked(true)}
        />
        {!frameLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/30 pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border-t bg-background/80 text-center"
      >
        Can't see the document? Open in a new tab
      </a>
    </div>
  );
}

/**
 * Shown when our PDF reader cannot open the file. Deliberately not a downgrade
 * to the browser's reader: the session needs page control and page text.
 */
function PdfFailureCta({
  url,
  failure,
  onRetry,
  className,
}: {
  url: string;
  failure: PdfFailure;
  onRetry: () => void;
  className?: string;
}) {
  const reason =
    failure.stage === "worker"
      ? "The document reader couldn't start in this environment." +
        (failure.worker?.contentType
          ? ` (reader file served as ${failure.worker.contentType})`
          : "")
      : failure.stage === "load"
        ? "The document couldn't be downloaded."
        : "The document couldn't be drawn.";

  return (
    <div className={"flex h-full w-full items-center justify-center bg-muted/30 p-6 " + (className ?? "")}>
      <div className="flex flex-col items-center gap-3 text-center max-w-sm">
        <FileText className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          This PDF can't be shown with page controls.
        </p>
        <p className="text-xs text-muted-foreground">{reason}</p>
        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" variant="secondary" onClick={onRetry} className="gap-1.5">
            <RotateCw className="h-3.5 w-3.5" />
            Try again
          </Button>
          <Button asChild size="sm" variant="ghost" className="gap-1.5">
            <a href={url} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              Open in a new tab
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function BlockedDocCta({ url, className }: { url: string; className?: string }) {
  return (
    <div className={"flex h-full w-full items-center justify-center bg-muted/30 p-6 " + (className ?? "")}>
      <div className="flex flex-col items-center gap-3 text-center max-w-sm">
        <FileText className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          This document can't be embedded here.
        </p>
        <p className="text-xs text-muted-foreground break-all">{url}</p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open in a new tab
        </a>
      </div>
    </div>
  );
}